import type { EncryptedQuiz, MediaRef } from '@quiz/shared';
import { unsealMedia } from '@quiz/shared';

/**
 * Sealed media, held on the handset from the door.
 *
 * The Cache API rather than localStorage, which is strings only and about 5MB;
 * a single video question is 2MB and a night could be forty. Cache stores
 * Responses, survives a reload, and is per-origin like everything else here.
 *
 * What is stored is the CIPHERTEXT. The phone holds it for half an hour before
 * the key arrives with the host's Open, which is the whole point: the
 * blip-resilience of caching without letting anyone read ahead. A player who
 * digs the bytes out of the cache has a blob of noise.
 */
const CACHE = 'fast-quiz/media/v1';

/** Cache keys must be URLs; this is never fetched, only used as a name. */
function keyFor(eventId: string, questionId: string): string {
  // Scoped by event: the same question sealed for two events is two different
  // ciphertexts, and serving one where the other belongs would simply fail to
  // decrypt -- confusingly, and in the middle of a round.
  return `/__media/${eventId}/${questionId}`;
}

async function openCache(): Promise<Cache | null> {
  // Private windows, storage pressure and a handful of embedded browsers all
  // make this throw or come back undefined. A phone with no cache still plays
  // the quiz; it just fetches media at Open instead of ahead of time.
  try {
    return 'caches' in globalThis ? await caches.open(CACHE) : null;
  } catch {
    return null;
  }
}

async function fetchSealed(questionId: string, token: string): Promise<Response> {
  const res = await fetch(`/api/media/${questionId}`, {
    headers: { authorization: `Bearer ${token}` },
    // No AbortSignal.timeout here on purpose. The 8s deadline in api() exists
    // because a hung POLL means a frozen quiz; a slow 2MB download on 4G is
    // ordinary and killing it at 8 seconds would make video unusable.
  });
  if (!res.ok) throw new Error(`media ${questionId}: ${res.status}`);
  return res;
}

/**
 * Every question in the quiz whose file THIS phone needs.
 *
 * In HOST audio mode the clips play over the venue's speaker, so the handsets
 * are sent none of them -- which is not a small saving dressed up as a feature:
 * it takes a music round from 27MB across the room to nothing at all, and it is
 * the reason the mode is worth having as well as the reason it exists.
 */
export function mediaQuestions(
  quiz: EncryptedQuiz,
  audioMode: 'HOST' | 'PLAYERS' = 'PLAYERS',
): { id: string; media: MediaRef }[] {
  return quiz.rounds.flatMap((r) =>
    r.questions.flatMap((q) =>
      q.media && !(audioMode === 'HOST' && q.media.kind === 'AUDIO')
        ? [{ id: q.id, media: q.media }]
        : [],
    ),
  );
}

export interface PrefetchProgress {
  done: number;
  total: number;
  bytesDone: number;
  bytesTotal: number;
  failed: number;
}

/**
 * Pull everything down, one at a time.
 *
 * Sequential, not parallel, and that is the deliberate part: 75 phones each
 * opening six connections at the door is how a pub access point falls over,
 * and nothing here is in a hurry. The arrival window is twenty minutes and the
 * heaviest night is a couple of hundred megabytes across the whole room.
 */
export async function prefetchMedia(
  quiz: EncryptedQuiz,
  eventId: string,
  token: string,
  audioMode: 'HOST' | 'PLAYERS',
  onProgress: (p: PrefetchProgress) => void,
  signal?: AbortSignal,
): Promise<void> {
  const wanted = mediaQuestions(quiz, audioMode);
  const bytesTotal = wanted.reduce((n, w) => n + w.media.bytes, 0);
  const cache = await openCache();

  let done = 0;
  let bytesDone = 0;
  let failed = 0;
  onProgress({ done, total: wanted.length, bytesDone, bytesTotal, failed });

  for (const { id, media } of wanted) {
    if (signal?.aborted) return;
    const url = keyFor(eventId, id);
    try {
      if (cache && (await cache.match(url))) {
        // Already held from a previous visit. Reloading the page mid-evening
        // must not re-download the night.
        done++;
        bytesDone += media.bytes;
        onProgress({ done, total: wanted.length, bytesDone, bytesTotal, failed });
        continue;
      }
      const res = await fetchSealed(id, token);
      if (cache) await cache.put(url, res.clone());
      // Read it even without a cache, so the progress bar is honest about the
      // work done rather than reporting instant success on a phone that will
      // have to fetch it all again at Open.
      await res.arrayBuffer();
      done++;
      bytesDone += media.bytes;
    } catch {
      // One failed file is not a failed quiz. The question still has its prompt
      // and its options; getSealed() will try again at Open.
      failed++;
    }
    onProgress({ done, total: wanted.length, bytesDone, bytesTotal, failed });
  }
}

/**
 * The sealed bytes for one question, from cache if they are there.
 *
 * The fallback is what makes a late arrival playable: somebody who joined five
 * minutes ago has not prefetched the video, so they fetch it now and see it a
 * few seconds late rather than not at all.
 */
export async function getSealed(
  eventId: string,
  questionId: string,
  token: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const cache = await openCache();
  const url = keyFor(eventId, questionId);
  const hit = cache ? await cache.match(url) : null;
  const res = hit ?? (await fetchSealed(questionId, token));
  if (!hit && cache) await cache.put(url, res.clone());
  return new Uint8Array(await res.arrayBuffer());
}

/**
 * Are the sealed bytes on this phone yet?
 *
 * Deliberately does NOT decrypt, and that distinction is the whole point of the
 * readiness signal: the key does not arrive until the host opens the question,
 * so anything that waited for decryption could never report ready while the
 * question was staged -- which is the only moment the host is actually asking.
 * The download is the slow part; unsealing 2MB is milliseconds.
 */
export async function isCached(eventId: string, questionId: string): Promise<boolean> {
  const cache = await openCache();
  if (!cache) return false;
  return Boolean(await cache.match(keyFor(eventId, questionId)));
}

/** Sealed bytes to something an <img>, <audio> or <video> can point at. */
export async function openMedia(
  eventId: string,
  questionId: string,
  token: string,
  key: string,
  mimeType: string,
): Promise<string> {
  const sealed = await getSealed(eventId, questionId, token);
  const plain = await unsealMedia(key, sealed);
  return URL.createObjectURL(new Blob([plain as BlobPart], { type: mimeType }));
}

/** Drop the whole night's media. Called when a player leaves the event. */
export async function clearMedia(): Promise<void> {
  try {
    if ('caches' in globalThis) await caches.delete(CACHE);
  } catch {
    /* nothing to clean up */
  }
}
